import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Chip, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Select, MenuItem, FormControl, InputLabel, Alert, Tooltip, Stack,
} from '@mui/material';
import {
    AutoAwesome, Refresh, PlayArrow, CloudUpload, CheckCircle,
    HourglassEmpty, Error as ErrorIcon, Schedule,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.autolistify.com';

const STATUS_CHIP = {
    collecting: { label: 'Collecting',  color: 'default',  icon: <Schedule sx={{ fontSize: 14 }} /> },
    pending:    { label: 'Generating',  color: 'primary',  icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
    done:       { label: 'Done',        color: 'success',  icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    partial:    { label: 'Partial',     color: 'warning',  icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    failed:     { label: 'Failed',      color: 'error',    icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
};

function statusChip(status) {
    const s = STATUS_CHIP[status] || { label: status, color: 'default', icon: null };
    return (
        <Chip
            label={s.label}
            color={s.color}
            size="small"
            icon={s.icon}
            sx={{ fontWeight: 600 }}
        />
    );
}

function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

export default function AIImageQueuePage() {
    const [stores,        setStores]        = useState([]);
    const [selectedStore, setSelectedStore] = useState('');
    const [jobs,          setJobs]          = useState([]);
    const [loading,       setLoading]       = useState(false);
    const [actionMsg,     setActionMsg]     = useState('');
    const [error,         setError]         = useState('');

    // Load stores
    useEffect(() => {
        axios.get(`${API_BASE}/api/stores`, { withCredentials: true })
            .then(r => {
                const s = r.data?.stores || r.data || [];
                setStores(s);
                if (s.length) setSelectedStore(s[0].id);
            })
            .catch(e => setError(e.message));
    }, []);

    const loadJobs = useCallback(async (storeId) => {
        if (!storeId) return;
        setLoading(true);
        setError('');
        try {
            const r = await axios.get(`${API_BASE}/api/batch-images/jobs`, {
                params: { storeId },
                withCredentials: true,
            });
            setJobs(r.data?.jobs || []);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (selectedStore) loadJobs(selectedStore);
    }, [selectedStore, loadJobs]);

    async function doAction(endpoint, body, successMsg) {
        setActionMsg('');
        setError('');
        try {
            await axios.post(`${API_BASE}/api/batch-images/${endpoint}`, body, { withCredentials: true });
            setActionMsg(successMsg);
            setTimeout(() => loadJobs(selectedStore), 2000);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        }
    }

    // Summary counts
    const counts = jobs.reduce((acc, j) => {
        acc[j.status] = (acc[j.status] || 0) + 1;
        return acc;
    }, {});

    const totalProducts = jobs.reduce((acc, j) => acc + (j.products?.length || 0), 0);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <AutoAwesome sx={{ color: 'primary.main', fontSize: 28 }} />
                <Box>
                    <Typography variant="h5" fontWeight={700}>AI Image Queue</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Gemini Batch API — 50% cheaper, async 2×2 grid generation
                    </Typography>
                </Box>
            </Box>

            {/* Store selector */}
            <FormControl size="small" sx={{ mb: 3, minWidth: 220 }}>
                <InputLabel>Store</InputLabel>
                <Select
                    value={selectedStore}
                    label="Store"
                    onChange={e => setSelectedStore(e.target.value)}
                >
                    {stores.map(s => (
                        <MenuItem key={s.id} value={s.id}>{s.storeName || s.storeIdentifier}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Stats */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
                {[
                    { label: 'Collecting', value: counts.collecting || 0, color: '#64748b' },
                    { label: 'Generating', value: counts.pending    || 0, color: '#4338ca' },
                    { label: 'Done',       value: counts.done       || 0, color: '#16a34a' },
                    { label: 'Failed',     value: counts.failed     || 0, color: '#dc2626' },
                    { label: 'Products',   value: totalProducts,           color: '#0f172a' },
                ].map(s => (
                    <Paper key={s.label} sx={{ px: 2.5, py: 1.5, minWidth: 100, textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={700} sx={{ color: s.color }}>{s.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                    </Paper>
                ))}
            </Stack>

            {/* Action buttons */}
            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap">
                <Tooltip title="Scan draft products for watermarks, Chinese text, or off-brand images">
                    <Button
                        variant="contained"
                        startIcon={<PlayArrow />}
                        onClick={() => doAction('scan', { storeId: selectedStore }, 'Scan started — check back in a few minutes')}
                        disabled={!selectedStore}
                    >
                        Scan Draft Products
                    </Button>
                </Tooltip>

                <Tooltip title="Submit all collected products to the Gemini Batch API now">
                    <Button
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        onClick={() => doAction('flush', { storeId: selectedStore }, 'Flush submitted — Gemini job will be created shortly')}
                        disabled={!selectedStore || !(counts.collecting > 0)}
                    >
                        Flush Queue ({counts.collecting || 0})
                    </Button>
                </Tooltip>

                <Tooltip title="Check Gemini for completed jobs and upload results to Shopify">
                    <Button
                        variant="outlined"
                        startIcon={<AutoAwesome />}
                        onClick={() => doAction('collect', {}, 'Checking Gemini jobs…')}
                        disabled={!selectedStore || !(counts.pending > 0)}
                    >
                        Collect Results ({counts.pending || 0})
                    </Button>
                </Tooltip>

                <Button
                    variant="text"
                    startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
                    onClick={() => loadJobs(selectedStore)}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Stack>

            {/* Messages */}
            {actionMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionMsg('')}>{actionMsg}</Alert>}
            {error     && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Jobs table */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : jobs.length === 0 ? (
                <Paper sx={{ p: 5, textAlign: 'center', color: 'text.secondary' }}>
                    <AutoAwesome sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography variant="h6">No jobs yet</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Enable <strong>Auto-queue on import</strong> in Settings, or click <em>Scan Draft Products</em> to start.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f8fafc' } }}>
                                <TableCell>Job</TableCell>
                                <TableCell>Products</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Submitted</TableCell>
                                <TableCell>Completed</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobs.map(job => (
                                <TableRow key={job.id} hover>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {job.displayName}
                                        </Typography>
                                        {job.batchName && (
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {job.batchName}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack spacing={0.5}>
                                            {(job.products || []).slice(0, 3).map(p => (
                                                <Typography key={p.id} variant="caption" noWrap sx={{ maxWidth: 220 }}>
                                                    {p.productTitle}
                                                    {' '}
                                                    <Chip
                                                        label={p.status}
                                                        size="small"
                                                        color={p.status === 'done' ? 'success' : p.status === 'failed' ? 'error' : 'default'}
                                                        sx={{ height: 16, fontSize: 10 }}
                                                    />
                                                </Typography>
                                            ))}
                                            {(job.products || []).length > 3 && (
                                                <Typography variant="caption" color="text.secondary">
                                                    +{job.products.length - 3} more
                                                </Typography>
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{statusChip(job.status)}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{fmtDate(job.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{fmtDate(job.submittedAt)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{fmtDate(job.completedAt)}</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
